import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { usePaginated } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { LoadMore } from '../components/Pagination';
import { Button, Card } from '../components/ui';
import { IconSearch } from '../components/Icons';

const PAGE = 25;

export function Search() {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');

  return (
    <>
      <PageHeader title={t('search.title')} subtitle={t('search.subtitle')} />
      <Card>
        <div className="card__body">
          <form
            role="search"
            className="form-row"
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(input.trim());
            }}
          >
            <div className="field" style={{ flex: 1, minWidth: 220, marginBottom: 0 }}>
              <label className="visually-hidden" htmlFor="search-q">
                {t('search.title')}
              </label>
              <input
                id="search-q"
                className="input"
                type="search"
                value={input}
                placeholder={t('search.placeholder')}
                onChange={(e) => setInput(e.target.value)}
              />
            </div>
            <Button type="submit" variant="primary">
              <IconSearch width={18} height={18} />
              {t('common.search')}
            </Button>
          </form>
        </div>
      </Card>

      {query ? (
        <div style={{ marginTop: 16 }}>
          <SearchResults query={query} />
        </div>
      ) : (
        <Card>
          <EmptyState title={t('search.prompt')} icon={<IconSearch width={26} height={26} />} />
        </Card>
      )}
    </>
  );
}

function SearchResults({ query }: { query: string }) {
  const { t } = useTranslation();
  const lang = useLang();
  const res = usePaginated((cursor) => api.search(query, PAGE, cursor), [query]);

  return (
    <Card>
      <ApiState status={res.status} error={res.error} data={res.status === 'error' ? null : res.items} onRetry={res.reload}>
        {(items) =>
          items.length === 0 ? (
            <EmptyState title={t('search.empty', { q: query })} icon={<IconSearch width={26} height={26} />} />
          ) : (
            <>
              <div className="card__head">
                <span className="count-note">{t('search.resultsCount', { count: items.length })}</span>
              </div>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>{t('search.colArchive')}</th>
                      <th>{t('search.colPath')}</th>
                      <th className="num">{t('common.size')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((hit, i) => (
                      <tr key={`${hit.archiveId}-${hit.path}-${i}`}>
                        <td className="cell-strong">
                          <Link to={`/archives/${hit.archiveId}`}>{hit.archiveName}</Link>
                        </td>
                        <td>{hit.path}</td>
                        <td className="num">{formatBytes(hit.bytes, lang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <LoadMore hasMore={res.hasMore} loading={res.loadingMore} onLoadMore={res.loadMore} />
            </>
          )
        }
      </ApiState>
    </Card>
  );
}
